import { useRef, useState } from 'react';
import { Bell, Loader2, Trash2 } from 'lucide-react';
import { Action, PushNotification } from '@the-real-insight/tri-model';

import { HoverCard } from '@/components/arc/hover-card/hover-card';
import { BRAND } from '@/brand/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import AlertMessagePreview from './AlertMessagePreview';
import AlertPushReachability from './AlertPushReachability';
import AlertPushRecipientPicker from './AlertPushRecipientPicker';
import PlaceholderPicker from './PlaceholderPicker';
import PlaceholderTextField, { PlaceholderTextFieldHandle } from './PlaceholderTextField';
import { placeholderBadge, Translate, unknownPlaceholderMessage, unknownPlaceholdersOfAction } from './AlertActionEditor';
import { ALERT_TEXT_NAMESPACE, placeholdersOf, placeholderTag } from './alertPlaceholders';
import { PushUsersState } from './usePushUsers';
import type { OwnRole } from './useOwnRoles';

const T = `${ALERT_TEXT_NAMESPACE}push.`;

/**
 * Keeps the fields of a push action in one place so every edit goes through the model's constructor.
 * "Kritischer Alarm" (SMS escalation) cannot be chosen any more, so an edit also switches off a flag a
 * rule saved earlier may still carry.
 */
const withChange = (action: PushNotification, change: Partial<PushNotification>): Action =>
    Action.unmarshal({ ...(action as any), ...change, critical: false });

/**
 * One push action of an alert rule: who is notified (roles and/or people), the headline and the text
 * with the same placeholders as e-mail, and below the recipients who of them can actually be reached.
 *
 * The headline is plain text (no placeholders): without one, the rule's name is used.
 */
export default function PushActionEditor(properties: {
    t: Translate;
    action: PushNotification;
    index: number;
    fieldId: string;
    testing: boolean;
    /** The rule's name: the headline when the action has none, shown in the preview. */
    ruleName: string;
    noUplink: boolean;
    deviceAlarm?: string;
    deviceAlarms?: string[];
    onDeviceAlarmChoose?: (alarm: string) => void;
    language?: string;
    users: PushUsersState;
    /**
     * Whether the signed-in user receives push themselves: "Test an mich" goes to their own devices, so with
     * `inactive` the button is off and says why. `unknown` (list not loaded or not available) leaves it on.
     */
    ownPush?: 'active' | 'inactive' | 'unknown';
    /** The user list is being loaded again (the check behind "Aktualisieren" in the hint). */
    ownPushChecking?: boolean;
    /** The customer's own roles, besides the three system roles. */
    ownRoles?: OwnRole[];
    /** Loads people and roles again (the "Aktualisieren" next to the groups of the recipient list). */
    onRefreshRecipients?: () => void;
    /** Show missing-recipient messages although nothing was touched (a save was tried). */
    showErrors: boolean;
    onChange: (action: Action) => void;
    onRemove: () => void;
    onTest: () => void;
}) {
    const { action, index, fieldId, t } = properties;
    const contentField = useRef<PlaceholderTextFieldHandle>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    // Set when "Aktualisieren" was pressed in the test hint: from then on the hint says if the check found no push.
    const [testChecked, setTestChecked] = useState(false);

    const content = action.content ?? '';
    const noRecipients = action.recipientRoles.length + action.recipientUsers.length === 0;
    const idBase = `${fieldId}-push-${index}`;
    const contentLabelId = `${idBase}-content-label`;
    const unknown = unknownPlaceholdersOfAction(action);

    const describe = (name: string) => placeholderBadge(name, t);

    return (
        <div className="min-w-0 rounded-lg border bg-card" data-testid={`push-action-${index}`}>
            <div className="flex min-w-0 items-center gap-2 border-b px-3 py-2">
                <Bell aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-sm font-semibold">{t(`${T}label`)}</span>
                {properties.showErrors && !action.isCompletelyDefined() && (
                    <span role="alert" className="min-w-0 truncate text-xs text-destructive">
                        {t(`${ALERT_TEXT_NAMESPACE}incompleteDefinition`)}
                    </span>
                )}
                <div className="ml-auto flex shrink-0 items-center gap-1">
                    {properties.ownPush === 'inactive' ? (
                        // A disabled button takes no hover: the tooltip sits on a focusable wrapper around it.
                        <HoverCard
                            side="bottom"
                            align="end"
                            openDelay={150}
                            content={
                                <div className="grid max-w-[16rem] gap-2 text-sm" data-testid="push-test-needs-app">
                                    <p className="font-semibold">{t(`${T}testNeedsAppTitle`)}</p>
                                    <p className="text-muted-foreground">{t(`${T}testNeedsApp`, { appName: BRAND.appName })}</p>
                                    {properties.onRefreshRecipients && (
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={properties.ownPushChecking}
                                                onClick={() => {
                                                    setTestChecked(true);
                                                    properties.onRefreshRecipients?.();
                                                }}
                                            >
                                                {properties.ownPushChecking && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                                                {t(`${T}${properties.ownPushChecking ? 'refreshing' : 'refresh'}`)}
                                            </Button>
                                            {testChecked && !properties.ownPushChecking && (
                                                <span role="status" className="text-xs text-muted-foreground">
                                                    {t(`${T}testStillNoPush`)}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            }
                        >
                            <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                                <Button variant="outline" size="sm" disabled aria-disabled className="pointer-events-none">
                                    {t(`${T}test`)}
                                </Button>
                            </span>
                        </HoverCard>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            title={t(`${T}testHint`)}
                            disabled={content.trim() === '' || properties.testing}
                            onClick={properties.onTest}
                        >
                            {properties.testing && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                            {properties.testing ? t(`${ALERT_TEXT_NAMESPACE}sending`) : t(`${T}test`)}
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t(`${ALERT_TEXT_NAMESPACE}removeAction`)}
                        title={t(`${ALERT_TEXT_NAMESPACE}removeAction`)}
                        onClick={properties.onRemove}
                    >
                        <Trash2 aria-hidden className="h-4 w-4" />
                    </Button>
                </div>
            </div>
            <div className="grid min-w-0 gap-4 p-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,300px)]">
                <div className="flex min-w-0 flex-col gap-4">
                    <AlertPushRecipientPicker
                        t={t}
                        idPrefix={idBase}
                        state={properties.users}
                        ownRoles={properties.ownRoles}
                        onRefresh={properties.onRefreshRecipients}
                        recipientRoles={action.recipientRoles}
                        recipientUsers={action.recipientUsers}
                        onChange={(recipientRoles, recipientUsers) =>
                            properties.onChange(withChange(action, { recipientRoles, recipientUsers }))
                        }
                    />
                    {properties.showErrors && noRecipients && (
                        <p role="alert" className="text-xs text-destructive">
                            {t(`${T}recipientRequired`)}
                        </p>
                    )}
                    <AlertPushReachability
                        t={t}
                        state={properties.users}
                        recipientRoles={action.recipientRoles}
                        recipientUsers={action.recipientUsers}
                    />

                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`${idBase}-title`}>{t(`${T}title`)}</Label>
                        <Input
                            id={`${idBase}-title`}
                            value={action.title ?? ''}
                            maxLength={100}
                            placeholder={properties.ruleName.trim() === '' ? t(`${T}titleDefault`) : properties.ruleName}
                            onChange={(event) =>
                                properties.onChange(withChange(action, { title: event.target.value === '' ? undefined : event.target.value }))
                            }
                        />
                        <p className="text-xs text-muted-foreground">{t(`${T}titleHint`)}</p>
                    </div>

                    <div className="flex flex-col gap-1">
                        <div className="flex min-h-7 items-center justify-between gap-2">
                            <Label id={contentLabelId} onClick={() => contentField.current?.focus()}>
                                {t(`${ALERT_TEXT_NAMESPACE}message`)}
                            </Label>
                            <PlaceholderPicker
                                t={t}
                                fieldLabel={t(`${ALERT_TEXT_NAMESPACE}message`)}
                                noUplink={properties.noUplink}
                                language={properties.language}
                                usedNames={placeholdersOf(content)}
                                open={pickerOpen}
                                onOpenChange={setPickerOpen}
                                onInsert={(name, keepOpen) =>
                                    contentField.current?.insertText(placeholderTag(name), { keepFocus: keepOpen })
                                }
                            />
                        </div>
                        <PlaceholderTextField
                            ref={contentField}
                            id={`${idBase}-content`}
                            labelledBy={contentLabelId}
                            multiline
                            value={content}
                            describe={describe}
                            onShortcut={() => setPickerOpen(true)}
                            onChange={(value) => properties.onChange(withChange(action, { content: value }))}
                        />
                    </div>
                    {unknown.map((problem) => (
                        <p key={problem.name} role="alert" className="text-sm text-destructive">
                            {unknownPlaceholderMessage(problem, t)}
                        </p>
                    ))}
                </div>
                <AlertMessagePreview
                    t={t}
                    kind="push"
                    language={properties.language}
                    deviceAlarm={properties.deviceAlarm}
                    deviceAlarms={properties.deviceAlarms}
                    onDeviceAlarmChoose={properties.onDeviceAlarmChoose}
                    title={(action.title ?? '').trim() === '' ? properties.ruleName : action.title}
                    content={content}
                />
            </div>
        </div>
    );
}
